import { useEffect, useState, useMemo } from "react";
import { http, API, tryParseJSON } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Search, FileText, Cat, Calendar, Sparkles, Download, ExternalLink, Copy } from "lucide-react";

const SECTIONS = [
    { key: "blueprints", icon: Sparkles, label_fr: "Blueprints", label_en: "Blueprints", tint: "text-[#FF4D00]" },
    { key: "characters", icon: Cat,      label_fr: "Personnages", label_en: "Characters", tint: "text-[#0099FF]" },
    { key: "episodes",   icon: FileText, label_fr: "Épisodes",   label_en: "Episodes",   tint: "text-[#FFB800]" },
    { key: "calendars",  icon: Calendar, label_fr: "Calendriers",label_en: "Calendars",  tint: "text-[#10B981]" },
];

function preview(str, n = 140) {
    if (!str) return "";
    const parsed = tryParseJSON(str);
    if (parsed) {
        const title = parsed.channel_name || parsed.title || parsed.name || parsed.concept;
        if (title) return title;
    }
    return str.slice(0, n) + (str.length > n ? "…" : "");
}

function formatDate(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso.slice(0, 10); }
}

export default function Library() {
    const { lang } = useApp();
    const [data, setData] = useState({ blueprints: [], characters: [], episodes: [], calendars: [] });
    const [busy, setBusy] = useState(true);
    const [q, setQ] = useState("");
    const [active, setActive] = useState("blueprints");

    const load = async (query = "") => {
        setBusy(true);
        try {
            const { data } = await http.get(`/library/search`, { params: { q: query } });
            setData(data);
        } catch (e) { toast.error("Library error"); }
        finally { setBusy(false); }
    };

    useEffect(() => { load(""); }, []);
    useEffect(() => { const t = setTimeout(() => load(q), 300); return () => clearTimeout(t); }, [q]);

    const counts = useMemo(() => Object.fromEntries(SECTIONS.map((s) => [s.key, (data[s.key] || []).length])), [data]);

    const downloadEpisode = async (id) => {
        const token = localStorage.getItem("sf_token");
        try {
            const r = await fetch(`${API}/export/episode/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `shorts-factory-${id.slice(0, 8)}.txt`;
            a.click(); URL.revokeObjectURL(url);
        } catch { toast.error("Download error"); }
    };

    const downloadCalendar = async (id) => {
        const token = localStorage.getItem("sf_token");
        try {
            toast.info(lang === "fr" ? "Préparation du bundle 30j…" : "Preparing 30-day bundle…");
            const r = await fetch(`${API}/export/calendar/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `shorts-factory-30days-${id.slice(0, 8)}.zip`;
            a.click(); URL.revokeObjectURL(url);
            toast.success(lang === "fr" ? "Bundle téléchargé" : "Bundle downloaded");
        } catch { toast.error("Download error"); }
    };

    const items = data[active] || [];

    return (
        <div data-testid="library-container" className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <div className="overline">10 · Library</div>
                    <h2 className="font-display text-2xl font-black uppercase mt-1">{lang === "fr" ? "Ma bibliothèque" : "My library"}</h2>
                    <p className="text-sm text-slate-400 mt-2">{lang === "fr" ? "Reprends une génération, télécharge un bundle ou le calendrier 30 jours complet." : "Pick up any past generation, download a bundle or the full 30-day calendar."}</p>
                </div>
                <div className="relative w-full md:w-80">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                        data-testid="library-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder={lang === "fr" ? "Recherche…" : "Search…"}
                        className="w-full pl-9 pr-3 py-2.5 bg-[#0F141C] border border-[#253046] rounded text-sm focus:outline-none focus:border-[#FF4D00]"
                    />
                </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {SECTIONS.map((s) => {
                    const Icon = s.icon;
                    const on = active === s.key;
                    return (
                        <button
                            key={s.key}
                            data-testid={`library-tab-${s.key}`}
                            onClick={() => setActive(s.key)}
                            className={`p-3 rounded border text-left kinetic ${on ? "border-[#FF4D00] bg-gradient-to-b from-[#1E2738] to-[#161D2A]" : "border-[#253046] bg-[#161D2A] hover:border-[#3B4B6C]"}`}
                        >
                            <div className="flex items-center gap-2">
                                <Icon className={`w-4 h-4 ${s.tint}`} />
                                <span className="font-display text-sm font-bold uppercase">{lang === "fr" ? s.label_fr : s.label_en}</span>
                            </div>
                            <div className="font-mono-sf text-xs text-slate-500 mt-1">{counts[s.key] || 0} {lang === "fr" ? "élément(s)" : "item(s)"}</div>
                        </button>
                    );
                })}
            </div>

            <div className="rounded border border-[#253046] bg-[#0B111B] p-4 min-h-[320px]">
                {busy && <div className="py-16 text-center text-slate-500 font-mono-sf text-xs">LOADING ···</div>}
                {!busy && items.length === 0 && (
                    <div className="py-16 text-center">
                        <p className="text-sm text-slate-500">{lang === "fr" ? "Aucun élément. Génère depuis les autres onglets et il apparaîtra ici." : "Nothing yet. Generate from other tabs and it will show up here."}</p>
                    </div>
                )}
                {!busy && items.length > 0 && (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {items.map((it) => {
                            const p = tryParseJSON(it.result);
                            const title = p?.channel_name || p?.title || p?.name || (it.input?.channel_name) || "Sans titre";
                            const sub = p?.concept || p?.one_liner || p?.appearance || it.input?.idea || preview(it.result);
                            return (
                                <div key={it.id} data-testid={`library-card-${active}`} className="bg-[#161D2A] border border-[#253046] hover:border-[#FF4D00]/50 kinetic rounded p-4 flex flex-col">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="font-display text-base font-bold uppercase leading-tight line-clamp-2">{title}</div>
                                        <div className="text-[10px] font-mono-sf text-slate-500 shrink-0">{formatDate(it.created_at)}</div>
                                    </div>
                                    <p className="text-xs text-slate-400 mt-2 line-clamp-3 flex-1">{sub}</p>
                                    <div className="mt-3 flex flex-wrap gap-1.5">
                                        <button
                                            onClick={() => { navigator.clipboard.writeText(it.result); toast.success("Copié"); }}
                                            className="text-[10px] px-2 py-1 rounded border border-[#253046] text-slate-300 hover:text-white hover:border-[#3B4B6C] flex items-center gap-1"
                                        >
                                            <Copy className="w-3 h-3" /> {lang === "fr" ? "Copier" : "Copy"}
                                        </button>
                                        {active === "episodes" && (
                                            <button
                                                data-testid="library-download-episode"
                                                onClick={() => downloadEpisode(it.id)}
                                                className="text-[10px] px-2 py-1 rounded bg-[#FF4D00] hover:brightness-110 text-black font-semibold flex items-center gap-1 kinetic"
                                            >
                                                <Download className="w-3 h-3" /> Bundle
                                            </button>
                                        )}
                                        {active === "calendars" && (
                                            <button
                                                data-testid="library-download-calendar"
                                                onClick={() => downloadCalendar(it.id)}
                                                className="text-[10px] px-2 py-1 rounded bg-[#FF4D00] hover:brightness-110 text-black font-semibold flex items-center gap-1 kinetic"
                                            >
                                                <Download className="w-3 h-3" /> ZIP 30j
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
